/**
 * "Decode" text reveal: letters appear outward from the center so the title
 * visibly grows from the middle, each new letter starting as a rapidly-changing
 * random glyph before settling on its true character. The whole run lasts about
 * one second. Used by the mission-complete / aircraft-destroyed overlay titles.
 */

const DEFAULT_CHARSET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!<>-_\\/[]{}=+*#%';

/**
 * Animate `el`'s text content from a centered scramble into `text`.
 *
 * @param {HTMLElement} el       element whose textContent is driven
 * @param {string} text          final title string
 * @param {object} [opts]
 * @param {number} [opts.duration=1000]  total animation length, ms
 * @param {string} [opts.charset]        glyphs used while scrambling
 * @returns {() => void} cancel function that stops the animation early
 */
export function scrambleReveal(el, text, opts = {}) {
  const duration = opts.duration ?? 1000;
  const charset = opts.charset ?? DEFAULT_CHARSET;
  const chars = [...text];
  const n = chars.length;
  const center = (n - 1) / 2;
  const maxDist = Math.max(center, 1);

  // Fraction of the timeline spent expanding outward from the center; the rest
  // lets the last-revealed (outermost) letters scramble before settling.
  const growthFrac = 0.6;
  const scrambleFrac = 0.4;

  // Per-letter activation (when it first appears) and settle (when it locks to
  // its true glyph) times. Distance from center drives activation so the string
  // grows symmetrically; a little random jitter staggers when letters "fall in".
  const meta = chars.map((ch, i) => {
    const d = Math.abs(i - center) / maxDist; // 0 at center → 1 at the edges
    const activate = d * growthFrac * duration;
    const settle = activate + scrambleFrac * duration * (0.6 + 0.4 * Math.random());
    return { ch, activate, settle };
  });

  const pick = () => charset[(Math.random() * charset.length) | 0];

  let raf = 0;
  let start = 0;

  const frame = (now) => {
    if (!start) start = now;
    const t = now - start;
    let out = '';
    for (const m of meta) {
      if (t < m.activate) continue;            // not yet revealed — keeps the string short + centered
      if (m.ch === ' ' || t >= m.settle) out += m.ch;
      else out += pick();
    }
    el.textContent = out;
    if (t < duration) {
      raf = requestAnimationFrame(frame);
    } else {
      el.textContent = text;                   // guarantee the exact final string
    }
  };

  raf = requestAnimationFrame(frame);
  return () => { if (raf) cancelAnimationFrame(raf); };
}
